// The handful of sprites the game needs that the pack does not ship, drawn with
// the pack's own primitives and palette so they sit in the same visual language.
//
// The pack and its three companion files (sprites.js, sprites-extras.js,
// sprites-creatures.js, sprites-world.js) now cover buildings, creatures, items,
// UI, FX and every season and biome. What is left here is game-specific: the
// generic woodland tree and its stump, a berry bush, the mine's rock and ore
// nodes, the tile cursor, the watering-can tiers the pack skips, the villager
// cast in every direction, and the table that says which sprite a tile swaps to
// when the season turns.
//
// Two rules from the pack's style contract still apply: ground tiles must be a
// pure function of (x, y) so they tile, and anything that is not a ground tile
// ends with outline(g, "K").
import { Lib, type Grid } from "../vendor/spritelib.ts";

const { G, px, rect, disc, outline, recolor, hash } = Lib;

export type Dir = "down" | "left" | "right" | "up";

export interface ExtraSprite {
  id: string;
  grid: Grid;
  /** draws on the blended FX layer rather than the alpha-tested ones */
  blended?: boolean;
}

const DIRS: Dir[] = ["down", "left", "right", "up"];

// ---------------------------------------------------------------- vegetation

/** Leafy woodland tree, 32x44, anchored bottom-centre. */
function tree(seed: number): Grid {
  const g = G(32, 44);
  rect(g, 13, 26, 6, 16, "b2");
  rect(g, 13, 26, 2, 16, "b3");
  rect(g, 18, 26, 1, 16, "b1");
  rect(g, 11, 40, 10, 2, "b1");
  px(g, 12, 41, "b2");
  px(g, 20, 41, "b2");
  disc(g, 16, 18, 14, 11, "g1");
  disc(g, 16, 16, 13, 10, "g2");
  const blobs: [number, number, number, number][] = [
    [10, 12, 6, 5],
    [22, 13, 6, 5],
    [16, 8, 7, 5],
    [16, 18, 8, 6],
  ];
  for (const [x, y, rx, ry] of blobs) disc(g, x, y, rx, ry, "g3");
  for (let i = 0; i < 14; i++) {
    const x = Math.floor(hash(i, 1, seed) * 26) + 3;
    const y = Math.floor(hash(i, 2, seed) * 18) + 3;
    if (g.d[y * g.w + x] === "g3") {
      px(g, x, y, "g4");
      px(g, x + 1, y, "g4");
    }
  }
  outline(g, "K");
  return g;
}

/** What is left after chopping, 24x18. */
function stump(): Grid {
  const g = G(24, 18);
  rect(g, 7, 8, 10, 7, "b2");
  rect(g, 7, 8, 2, 7, "b3");
  disc(g, 12, 8, 5.5, 2.5, "b3");
  disc(g, 12, 8, 3, 1.4, "b4");
  disc(g, 12, 8, 1.2, 0.8, "b2");
  rect(g, 7, 14, 10, 1, "b1");
  outline(g, "K");
  return g;
}

/** Berry bush, 24x24. `picked` drops the fruit and keeps the foliage. */
function bush(picked: boolean): Grid {
  const g = G(24, 24);
  disc(g, 12, 16, 9, 7, "g1");
  disc(g, 12, 15, 8, 6, "g2");
  disc(g, 9, 13, 4, 3, "g3");
  disc(g, 16, 15, 4, 3, "g3");
  for (let i = 0; i < 10; i++) {
    const x = Math.floor(hash(i, 3, 41) * 16) + 4;
    const y = Math.floor(hash(i, 7, 41) * 12) + 8;
    if (g.d[y * g.w + x] === "g3") px(g, x, y, "g4");
  }
  if (!picked) {
    for (const [x, y] of [
      [7, 14],
      [13, 12],
      [17, 16],
      [11, 18],
      [15, 20],
    ] as [number, number][]) {
      disc(g, x, y, 1.6, 1.6, "p1");
      px(g, x - 1, y - 1, "p2");
    }
  }
  outline(g, "K");
  return g;
}

// ------------------------------------------------------------- mine content

/** Mineable rock, 24x24. */
function rock(big: boolean): Grid {
  const g = G(24, 24);
  const r = big ? 9 : 6;
  disc(g, 12, 16, r, r * 0.72, "n1");
  disc(g, 12, 15, r - 1, r * 0.62, "n2");
  disc(g, 10 - (big ? 1 : 0), 13, r * 0.4, r * 0.3, "n3");
  for (let i = 0; i < 5; i++) px(g, 8 + i * 2, 18 + (i % 2), "n1");
  outline(g, "K");
  return g;
}

/** Ore node: a rock with coloured veins glinting out of it. */
function ore(kind: "copper" | "iron" | "gold"): Grid {
  const g = rock(false);
  const vein = kind === "copper" ? "r2" : kind === "iron" ? "n3" : "y1";
  const glint = kind === "copper" ? "y1" : kind === "iron" ? "w" : "y2";
  for (const [x, y] of [
    [9, 14],
    [13, 13],
    [15, 17],
    [10, 18],
  ] as [number, number][]) {
    disc(g, x, y, 1.4, 1.2, vein);
    px(g, x, y - 1, glint);
  }
  outline(g, "K");
  return g;
}

// -------------------------------------------------------------------- cursor

/** Corner brackets used as the "tile you are about to work" cursor, 24x24. */
function cursor(): Grid {
  const g = G(24, 24);
  const corner = (x: number, y: number, dx: number, dy: number) => {
    for (let i = 0; i < 6; i++) {
      px(g, x + dx * i, y, "w");
      px(g, x, y + dy * i, "w");
    }
  };
  corner(1, 1, 1, 1);
  corner(22, 1, -1, 1);
  corner(1, 22, 1, -1);
  corner(22, 22, -1, -1);
  return g;
}

// ------------------------------------------------------------- villager cast

/**
 * The pack recolours the farmer rig into a cast, but only facing the camera.
 * These are the same maps applied to every direction and frame, so a villager
 * inherits the walk cycle, the hat and the hair for the price of a lookup.
 */
export const NPC_PALETTES: Record<string, Record<string, string>> = {
  villager: { r1: "g2", r2: "g3", c1: "b2", D: "b1" },
  shopkeeper: { r1: "p1", r2: "p2", c1: "n1", D: "K", b4: "w", b3: "n3" },
  spouse: { r1: "y1", r2: "y2", c1: "p1", D: "p1", b1: "r1" },
  child: { r1: "c2", r2: "w", c1: "g2", D: "g1" },
};

// ------------------------------------------------------------------ seasons

const SEASON_MAPS: Record<string, Record<string, string>> = {
  summer: { g1: "g2", g2: "g3", g3: "g4", g4: "y2" },
  autumn: { g1: "b2", g2: "b3", g3: "y1", g4: "y2" },
  winter: { g1: "b1", g2: "b2", g3: "n3", g4: "w" },
};

const EDGES = ["n", "e", "s", "w", "ne", "nw", "se", "sw"];
const GRASS_EDGES = [...EDGES, "in_ne", "in_nw", "in_se", "in_sw"];

/**
 * What a sprite becomes when the season turns, keyed `"<id>|<season>"`. World
 * consults this rather than guessing a suffix, so anything without an entry
 * simply keeps its spring look all year.
 */
export const SEASON_VARIANT = new Map<string, string>();

function variant(id: string, season: string, to: string): void {
  SEASON_VARIANT.set(`${id}|${season}`, to);
}

// ------------------------------------------------------------------ catalog

export function extraSprites(base: Map<string, Grid>): ExtraSprite[] {
  const out: ExtraSprite[] = [];
  const add = (id: string, grid: Grid, blended = false) => {
    if (base.has(id)) return; // the pack already ships it
    out.push({ id, grid, blended });
  };
  const grid = (id: string): Grid | undefined => base.get(id) ?? out.find((e) => e.id === id)?.grid;

  // ---- woodland, forage and the mine
  add("tree_a", tree(3));
  add("tree_b", tree(11));
  add("stump", stump());
  add("bush_berry", bush(false));
  add("bush_berry_picked", bush(true));
  add("ui_cursor", cursor());
  add("rock_small", rock(false));
  add("rock_large", rock(true));
  for (const k of ["copper", "iron", "gold"] as const) add(`ore_${k}`, ore(k));

  // ---- the pack tiers hoe, axe, pickaxe and scythe but not the can
  for (const [tier, map] of Object.entries(Lib.TIERS)) {
    add(`tool_wateringcan_${tier}`, recolor(Lib.tool("wateringcan"), map));
  }

  // ---- the cast, in every direction the pack does not already cover
  for (const [who, map] of Object.entries(NPC_PALETTES)) {
    for (const dir of DIRS) {
      for (let f = 0; f < 2; f++) {
        add(`${who}_idle_${dir}_${f}`, recolor(Lib.farmer({ dir, pose: "idle", bob: f }), map));
      }
      for (let f = 0; f < 4; f++) {
        add(
          `${who}_walk_${dir}_${f}`,
          recolor(Lib.farmer({ dir, pose: "walk", legs: f, bob: f % 2 ? 0 : 1 }), map),
        );
      }
    }
  }

  // ---- seasons -------------------------------------------------------------
  //
  // Plain grass, the frozen pond and the bare winter trees all come straight out
  // of the pack. The autotile edges do not, so they are recoloured here: the
  // season maps only touch the green keys, which leaves the dirt half of a
  // grass→dirt transition exactly as it was.

  for (let i = 1; i <= 3; i++) {
    const id = `grass_0${i}`;
    variant(id, "summer", `grass_summer_0${i}`);
    variant(id, "autumn", `grass_autumn_0${i}`);
    variant(id, "winter", `snow_0${i}`);
  }

  const dirt = grid("dirt_path");
  const snow = grid("snow_01");
  for (const d of GRASS_EDGES) {
    const id = `grass_to_dirt_${d}`;
    const src = grid(id);
    if (!src) continue;
    for (const season of ["summer", "autumn"]) {
      const to = `${id}_${season}`;
      add(to, recolor(clone(src), SEASON_MAPS[season]!));
      variant(id, season, to);
    }
    // winter lays snow rather than dead grass, so the edge is regenerated
    if (dirt && snow) {
      const to = `${id}_winter`;
      add(to, Lib.__transition(() => clone(snow), () => clone(dirt), toPackDir(d)));
      variant(id, "winter", to);
    }
  }

  for (const d of EDGES) {
    const id = `water_edge_${d}`;
    const src = grid(id);
    if (!src) continue;
    for (const season of ["summer", "autumn"]) {
      const to = `${id}_${season}`;
      add(to, recolor(clone(src), SEASON_MAPS[season]!));
      variant(id, season, to);
    }
    // the pond freezes over: the pack already draws ice meeting snow
    variant(id, "winter", `ice_edge_${d}`);
  }
  variant("water", "winter", "ice_water");

  for (const [id, bare] of [
    ["tree_a", "tree_apple_4"],
    ["tree_b", "tree_cherry_4"],
  ] as const) {
    const src = grid(id)!;
    for (const season of ["summer", "autumn"]) {
      const to = `${id}_${season}`;
      add(to, recolor(clone(src), SEASON_MAPS[season]!));
      variant(id, season, to);
    }
    variant(id, "winter", bare);
  }

  variant("soil_dry", "winter", "soil_frozen");

  return out;
}

function clone(g: Grid): Grid {
  return { w: g.w, h: g.h, d: g.d.slice() };
}

/** The pack's edge directions are upper case, with inner corners as `IN_*`. */
function toPackDir(d: string): string {
  return d.toUpperCase();
}

export { recolor };
